import type { CountryCode } from 'libphonenumber-js';
import { Metadata } from 'libphonenumber-js';
import {
	AsYouType,
	getCountryCallingCode,
	isValidPhoneNumber,
	parsePhoneNumberFromString,
} from 'libphonenumber-js/max';

/**
 * Wraps libphonenumber-js validation for length and valid digits
 */
export function isPhoneNumberValid(number: string, countryCode: CountryCode = 'US'): boolean {
	return isValidPhoneNumber(number, countryCode);
}

/**
 * Returns the country an E.164-formatted phone number resolves to, or undefined if it can't
 * be determined. Unlike `isPhoneNumberValid`, this doesn't take a country hint: for a
 * `+`-prefixed number, the number's own calling code always determines its country.
 */
export function getPhoneNumberCountry(number: string): CountryCode | undefined {
	return parsePhoneNumberFromString(number)?.country;
}

/**
 * Returns the max possible length of a phone number for a specific country.
 *
 * The length includes the country's calling code, the national number, and the + sign.
 * @param {*} country country code
 */
export function getMaxPhoneNumberLengthPerCountry(country: CountryCode = 'US'): number {
	const countryCallingCode = getCountryCallingCode(country);
	// TODO: Refactor to use supported methods (i.e., selectNumberingPlan)

	const metadata = new Metadata();
	metadata.selectNumberingPlan(country);

	const possibleLengths = metadata.numberingPlan?.possibleLengths() ?? [];
	const maxNationalNumberLength = Number(possibleLengths[possibleLengths.length - 1]);
	const maxPossibleLength = maxNationalNumberLength + countryCallingCode.length + 1;

	return maxPossibleLength;
}

/**
 * Returns an instance of the AsYouType class, which does inline formatting.
 *
 * @param {*} numString user input for phone number
 * @param {*} country country code
 */
export function formatPhoneNumber(numString: string, country: CountryCode = 'US'): string {
	if (numString.length <= 2) {
		return new AsYouType(country).input(numString);
	}

	const countryCallingCode = getCountryCallingCode(country);
	const phoneNumberString = numString.startsWith(countryCallingCode)
		? numString.replace(countryCallingCode, '')
		: numString;
	const parsedPhoneNumber = parsePhoneNumberFromString(phoneNumberString, country);

	if (parsedPhoneNumber === undefined) {
		return new AsYouType(country).input(phoneNumberString);
	}

	const { number } = parsedPhoneNumber;
	const maxPossibleLength = getMaxPhoneNumberLengthPerCountry(country);
	const phoneNumber = number.slice(0, maxPossibleLength);
	return new AsYouType(country).input(phoneNumber);
}
